import React, { useState, useEffect } from "react";
import { Users, Check, Search, X } from "lucide-react";
import { Typography, Box, Button, Avatar, Modal } from "./UIComponents";
import axios from "axios"

const AgentAssignmentModal = ({
  open,
  onClose,
  agentList = [],
  currentAgentId,
  leadId,
  onAssignSuccess,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredAgents, setFilteredAgents] = useState(agentList);
  const [currentAgent, setCurrentAgent] = useState(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  useEffect(() => {
    const filtered = searchQuery
      ? agentList.filter((a) =>
          a.full_name.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : agentList;
    setFilteredAgents(filtered);
  }, [searchQuery, agentList]);

  useEffect(() => {
    const agent = agentList.find((a) => a.id === currentAgentId) || null;
    setCurrentAgent(agent);
  }, [currentAgentId, agentList]);
  const handleAssignAgent = (agentId) => {
    const payload = {
      lead_id: leadId,
      user_id: agentId,
    };
    const agent = agentList.find((a) => a.id === agentId);

    axios
      .post(`${BASE_URL}/leads/assignLead`, payload, { withCredentials: true })
      .then((response) => {
        onAssignSuccess(agent);
        onClose();
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="assign-agent-modal">
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm"
          onClick={onClose}
        ></div>

        <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 z-50 transform transition-all duration-300 ease-in-out">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-full">
                <Users size={22} className="text-blue-600" />
              </div>
              <Typography variant="h6" className="text-blue-900 font-semibold">
                {currentAgentId ? "Update Agent Assignment" : "Assign Agent"}
              </Typography>
            </div>

            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 p-2 rounded-full transition-colors focus:outline-none"
              aria-label="Close modal"
            >
              <X size={18} />
            </button>
          </div>

          {/* Current assignment notice */}
          {currentAgent && (
            <div className="bg-blue-50 px-6 py-3 border-b border-blue-100">
              <div className="flex items-center gap-2">
                <Check size={16} className="text-blue-600" />
                <Typography variant="body2" className="text-blue-700">
                  Currently assigned to:{" "}
                  <span className="font-semibold">
                    {currentAgent.full_name}
                  </span>
                </Typography>
              </div>
            </div>
          )}

          {/* Search box */}
          <div className="px-6 pt-5 pb-3">
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all duration-200"
              />
              <Search
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
            </div>

            <Typography variant="body2" className="text-gray-600 mb-2">
              {currentAgentId
                ? "Select a new agent to reassign this lead:"
                : "Choose an agent to handle this lead:"}
            </Typography>
          </div>

          {/* Agent list */}
          <div className="px-6 pb-6 max-h-[280px] overflow-y-auto custom-scrollbar">
            {filteredAgents.length > 0 ? (
              <div className="space-y-2">
                {filteredAgents.map((agent) => {
                  const isAssigned = agent.id === currentAgentId;
                  return (
                    <button
                      key={agent.id}
                      onClick={() => handleAssignAgent(agent.id)}
                      className={`w-full text-left transition-all duration-200 p-3 rounded-lg border ${
                        isAssigned
                          ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-600"
                          : "bg-white hover:bg-blue-50 border-gray-200 hover:border-blue-300"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar
                          initials={agent.full_name.charAt(0)}
                          size="sm"
                          backgroundColor={isAssigned ? "gray" : "blue"}
                          className={isAssigned ? "bg-white text-blue-600" : ""}
                        />
                        <div className="flex-1">
                          <Typography
                            variant="subtitle2"
                            className={`font-medium ${
                              isAssigned ? "text-white" : "text-gray-800"
                            }`}
                          >
                            {agent.full_name}
                          </Typography>
                          {agent.role && (
                            <Typography
                              variant="caption"
                              className={
                                isAssigned ? "text-blue-100" : "text-gray-500"
                              }
                            >
                              {agent.role}
                            </Typography>
                          )}
                        </div>
                        {isAssigned && (
                          <Check size={16} className="text-white" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center">
                <Typography variant="body2" className="text-gray-500">
                  No agents found matching "{searchQuery}"
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AgentAssignmentModal;
